// src/oura/components/onboarding/UnmatchedAnswers.tsx
//
// Form answers that match no client (#3977).
//
// The website on the kick-off form is prefilled but editable. A client who
// types a website of their own has their answer stored with no business, and
// until this card nothing on any screen said so: their Onboarding tab kept
// reading "Waiting for them to answer the form" while the answer sat in the
// table. It was found by querying the database.
//
// So the notice is deliberately on the Businesses list, where staff start:
// seeing it must not require opening the right client, because the whole
// problem is that nobody knows which client it belongs to.
//
// Reads nothing it does not show, and decides nothing: the rows and the
// onboarding records are the server's, and filing one is one POST.
//
// An answer that belongs to no client (a test from our own team, a typo in the
// website) can be dismissed with a reason instead (#4641, backend #6454): it is
// kept on the server and leaves this list, so the notice does not stay on for good.

import { useState } from "react";
import { InboxIcon } from "lucide-react";
import { assignUnmatchedAnswer, dismissUnmatchedAnswer, type AnswerDismissal, type Onboarding, type UnmatchedAnswer } from "@service/aeo/onboarding";
import { useSnackbar } from "@hook/use-snackbar";
import { cn } from "../../lib/cn";
import { Drawer, DrawerBlock, FOCUS_RING, IconSquare, INLINE_PILL, PillButton, SURFACE, TYPE } from "@oura/ui";
import { assignTargets, dayTime, unmatchedNoticeText, type AssignTarget } from "../../onboarding/derive";
import { Field, Refusal } from "./fields";
import { useWritesPaused } from "@context/maintenance";

/**
 * The notice, and nothing at all when there is nothing waiting.
 *
 * An empty list and a failed read look the same here on purpose: the service
 * answers [] either way, and a count that cannot be trusted must not be shown.
 */
export function UnmatchedAnswersNotice({
    rows,
    onboardings,
    onFiled,
    onDismissed,
}: {
    rows: UnmatchedAnswer[];
    onboardings: Record<string, Onboarding>;
    /** The answer landed on a client: the caller re-reads the list and the records. */
    onFiled: (onboarding: Onboarding) => void;
    /** The answer was dismissed: the caller re-reads the list (#4641). */
    onDismissed: () => void;
}) {
    const [open, setOpen] = useState(false);
    if (rows.length === 0) return null;
    return (
        <>
            {/* The house card, not a tinted banner: the amber square says it waits
                on us, and the card stays as calm as everything under it. */}
            <button
                type="button"
                onClick={() => setOpen(true)}
                className={cn(SURFACE.card, "mb-6 flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50", FOCUS_RING)}
            >
                <IconSquare tone="amber" icon={InboxIcon} />
                <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-900">{unmatchedNoticeText(rows.length)}</span>
                    <span className={cn(TYPE.tc, "mt-0.5 block text-gray-500")}>
                        Someone answered the form with a website Oura does not have on a client. File each one on the client it belongs to.
                    </span>
                </span>
                <span className={cn(INLINE_PILL.base, INLINE_PILL.secondary, INLINE_PILL.tile)}>Open</span>
            </button>
            <UnmatchedAnswersDrawer
                open={open}
                rows={rows}
                targets={assignTargets(onboardings)}
                onClose={() => setOpen(false)}
                onFiled={(o) => {
                    onFiled(o);
                    // The last one closes the drawer: an empty list has nothing to show.
                    if (rows.length <= 1) setOpen(false);
                }}
                onDismissed={() => {
                    onDismissed();
                    if (rows.length <= 1) setOpen(false);
                }}
            />
        </>
    );
}

function UnmatchedAnswersDrawer({
    open,
    rows,
    targets,
    onClose,
    onFiled,
    onDismissed,
}: {
    open: boolean;
    rows: UnmatchedAnswer[];
    targets: AssignTarget[];
    onClose: () => void;
    onFiled: (onboarding: Onboarding) => void;
    onDismissed: () => void;
}) {
    return (
        <Drawer open={open} onClose={onClose} title="Form answers with no client" sub="Each one was answered with a website Oura does not have. Pick the client it belongs to." width={620}>
            {targets.length === 0 && (
                <DrawerBlock label="Nowhere to file them yet">
                    No client is waiting for form answers. Create the client first, check whether this answer has already been filed on one, or dismiss it if it belongs to no client.
                </DrawerBlock>
            )}
            <ul className="m-0 list-plain space-y-3 p-0">
                {rows.map((row) => (
                    <li key={row.id}>
                        <UnmatchedRow row={row} targets={targets} onFiled={onFiled} onDismissed={onDismissed} />
                    </li>
                ))}
            </ul>
        </Drawer>
    );
}

function UnmatchedRow({
    row,
    targets,
    onFiled,
    onDismissed,
}: {
    row: UnmatchedAnswer;
    targets: AssignTarget[];
    onFiled: (onboarding: Onboarding) => void;
    onDismissed: () => void;
}) {
    const snackbar = useSnackbar();
    const [picked, setPicked] = useState("");
    const [busy, setBusy] = useState(false);
    // A dismiss in flight holds the row too: one answer is filed OR dismissed, by the action clicked first (Jack, #4645).
    const [dismissing, setDismissing] = useState(false);
    const [refused, setRefused] = useState<string | null>(null);

    const file = async () => {
        if (!picked || busy || dismissing) return;
        setBusy(true);
        setRefused(null);
        try {
            const r = await assignUnmatchedAnswer(row.id, picked);
            if (!r.ok) {
                setRefused(r.message);
                return;
            }
            const target = targets.find((t) => t.businessId === picked);
            snackbar.showSuccess("Answer filed", `${target?.name ?? "The client"} now has their form answers.`);
            onFiled(r.onboarding);
        } catch {
            setRefused("The answer was not filed. Check your connection and try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className={cn(SURFACE.block, "space-y-3")}>
            <div className="space-y-1">
                {/* The three facts that identify an answer: what they typed is first, because it is why this did not match. */}
                <p className={cn(TYPE.tb, "m-0 break-words font-medium text-gray-900")}>{row.website?.trim() || "No website given"}</p>
                <p className={cn(TYPE.tc, "m-0 break-words text-gray-600")}>{row.respondentEmail?.trim() || "No email given"}</p>
                <p className={cn(TYPE.tc, "m-0 text-gray-500")}>Answered {dayTime(row.submittedAt)}</p>
            </div>
            <Field label="File it on" hint="Only clients still waiting for a form. One that has answered is not offered: filing over its answers would destroy them.">
                {({ id, describedBy }) => (
                    <select
                        id={id}
                        aria-describedby={describedBy}
                        value={picked}
                        disabled={busy || dismissing || targets.length === 0}
                        onChange={(e) => {
                            setPicked(e.target.value);
                            setRefused(null);
                        }}
                        className={cn(TYPE.tb, "block h-11 w-full rounded-xl border-0 bg-gray-50 px-3 text-gray-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 sm:h-9")}
                    >
                        <option value="">Pick a client</option>
                        {targets.map((t) => (
                            <option key={t.businessId} value={t.businessId}>
                                {t.name} · {t.domain}
                            </option>
                        ))}
                    </select>
                )}
            </Field>
            {refused && <Refusal>{refused}</Refusal>}
            <PillButton variant="primary" busy={busy} disabled={!picked || busy || dismissing} writes onClick={() => void file()}>
                File it on this client
            </PillButton>
            <DismissAnswer
                answerId={row.id}
                disabled={busy}
                onBusyChange={setDismissing}
                onDismissed={() => {
                    snackbar.showSuccess("Answer dismissed", `${row.website?.trim() || "The answer"} is no longer waiting for a client.`);
                    onDismissed();
                }}
            />
        </div>
    );
}

/**
 * Dismiss an answer that belongs to no client, with a reason (#4641, backend
 * #6454). The reason is required, so the answer's record says why it was set
 * aside; the server keeps the answer. Shared with the Form answers screen.
 */
export function DismissAnswer({
    answerId,
    disabled = false,
    onDismissed,
    onBusyChange,
    dismiss = async (id, reason) => {
        const r = await dismissUnmatchedAnswer(id, reason);
        return r.ok ? { ok: true as const, dismissed: r.answer.dismissed ?? null } : { ok: false as const, message: r.message };
    },
}: {
    answerId: string;
    /** The parent's own action is in flight: nothing is sent from here meanwhile. */
    disabled?: boolean;
    onDismissed: (dismissed: AnswerDismissal | null) => void;
    /** Told when a dismiss starts and ends, so the parent holds its own action meanwhile (Jack, #4645). */
    onBusyChange?: (busy: boolean) => void;
    /** The call; the Form answers screen passes its own, which settles its table. */
    dismiss?: (id: string, reason: string) => Promise<{ ok: true; dismissed: AnswerDismissal | null } | { ok: false; message: string }>;
}) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [refused, setRefused] = useState<string | null>(null);
    const typed = reason.trim();
    const writesPaused = useWritesPaused();

    const go = async () => {
        if (!typed || busy || disabled || writesPaused) return;
        setBusy(true);
        onBusyChange?.(true);
        setRefused(null);
        try {
            const r = await dismiss(answerId, typed);
            if (r.ok) onDismissed(r.dismissed);
            else setRefused(r.message);
        } catch {
            setRefused("The answer was not dismissed. Check your connection and try again.");
        } finally {
            setBusy(false);
            onBusyChange?.(false);
        }
    };

    return (
        <div className="space-y-3 border-t border-gray-100 pt-3" data-dismiss-answer={answerId}>
            <Field label="Not a client? Dismiss it" hint="Say why, such as a test answer or a mistyped website. The answer is kept; it only leaves this list.">
                {({ id, describedBy }) => (
                    <input
                        id={id}
                        aria-describedby={describedBy}
                        value={reason}
                        maxLength={200}
                        disabled={busy || disabled}
                        placeholder="Test answer"
                        onChange={(e) => {
                            setReason(e.target.value);
                            setRefused(null);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") void go();
                        }}
                        className={cn(TYPE.tb, "block h-11 w-full rounded-xl border-0 bg-gray-50 px-3 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 sm:h-9")}
                    />
                )}
            </Field>
            {refused && <Refusal>{refused}</Refusal>}
            <PillButton variant="secondary" busy={busy} disabled={!typed || disabled} writes onClick={() => void go()}>
                Dismiss
            </PillButton>
        </div>
    );
}
